import { ArgumentException } from '../exceptions.js';
import { Discount, DiscountType } from '../models/serializable/discounts/discount.js';
import { Price } from '../models/serializable/price.js';

export interface DiscountModifier {
  discount: Discount;
  availableUses: number;
}

export abstract class DiscountProvider {
  abstract findDiscountForMultipleTickets(ticketId: string, quantity: number): DiscountModifier | null;
  abstract findDiscountForSingleTicket(ticketId: string): Discount | null;
  abstract findDiscountForOrder(orderPrice: Price): Discount | null;
  abstract reset(): void;
}

export class NoDiscountProvider extends DiscountProvider {
  findDiscountForMultipleTickets(_ticketId: string, _quantity: number): DiscountModifier | null {
    return null;
  }

  findDiscountForSingleTicket(_ticketId: string): Discount | null {
    return null;
  }

  findDiscountForOrder(_orderPrice: Price): Discount | null {
    return null;
  }

  reset(): void {
    // Nothing to do here.
  }
}

export abstract class CartDiscountProvider extends DiscountProvider {
  static create(cartDiscount: Discount): CartDiscountProvider {
    return cartDiscount.type === DiscountType.Percentage ?
        PercentageCartDiscountProvider.create(cartDiscount):
        FixedCartDiscountProvider.create(cartDiscount);
  }
}

class FixedCartDiscountProvider extends CartDiscountProvider {
  private constructor(
      private readonly cartDiscount: Discount,
  ) {super();}

  static create(cartDiscount: Discount): FixedCartDiscountProvider {
    if(cartDiscount.type === DiscountType.Percentage) {
      throw new ArgumentException(`Discount type {${DiscountType.Percentage}} is not supported.`);
    }

    return new FixedCartDiscountProvider(cartDiscount);
  }

  findDiscountForMultipleTickets(_ticketId: string, _quantity: number): DiscountModifier | null {
    return null;
  }

  findDiscountForSingleTicket(_ticketId: string): Discount | null {
    return null;
  }

  findDiscountForOrder(_orderPrice: Price): Discount | null {
    return this.cartDiscount;
  }

  reset(): void {
    // Nothing to do here.
  }
}

class PercentageCartDiscountProvider extends CartDiscountProvider {
  private constructor(
      private readonly cartDiscount: Discount,
  ) {super();}

  static create(cartDiscount: Discount): PercentageCartDiscountProvider {
    if(cartDiscount.type !== DiscountType.Percentage) {
      throw new ArgumentException(`Discount type not supported. Expected {${DiscountType.Percentage}} but got {${cartDiscount.type}}.`);
    }

    return new PercentageCartDiscountProvider(cartDiscount);
  }

  findDiscountForMultipleTickets(_ticketId: string, quantity: number): DiscountModifier | null {
    return { discount: this.cartDiscount, availableUses: quantity };
  }

  findDiscountForSingleTicket(_ticketId: string): Discount | null {
    return this.cartDiscount;
  }

  findDiscountForOrder(_orderPrice: Price): Discount | null {
    return null;
  }

  reset(): void {
    // Nothing to do here.
  }
}
